import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import {
  codingProblemDetailSchema,
  codingProblemListSchema,
  codingSessionDetailSchema,
  codingSessionListSchema,
  currentUserSchema,
  dashboardSchema,
  healthSchema,
  interviewCatalogSchema,
  interviewDetailSchema,
  interviewListSchema,
  mentorCatalogSchema,
  mentorSessionDetailSchema,
  mentorSessionListSchema,
  profileSchema,
  publicLeaderboardSchema,
  readinessSchema,
  resumeDetailSchema,
  resumeDownloadSchema,
  resumeListSchema,
  roadmapDetailSchema,
  roadmapListSchema,
  companyDetailSchema,
  companyListSchema,
  adminMeSchema,
  adminPresenceSchema,
  adminAnalyticsSchema,
  adminSystemSchema,
  adminAuditLogListSchema,
  adminUserListSchema,
  adminUserListItemSchema,
  adminUserDetailSchema,
  analyticsEventIngestSchema,
  presenceHeartbeatSchema,
  userStatsSchema,
  type AccountStatus,
  type AdminAnalytics,
  type AdminAuditLogList,
  type AdminAuditLogQuery,
  type AdminMe,
  type AdminPresence,
  type AdminSystem,
  type AdminUserDetail,
  type AdminUserList,
  type AdminUserListItem,
  type AdminUsersQuery,
  type AnalyticsEventPayload,
  type AnalyticsEventIngest,
  type AppRole,
  type PresenceHeartbeat,
  type PresenceHeartbeatPayload,
  type AnswerSubmit,
  type CodingProblemDetail,
  type CodingProblemList,
  type CodingRunRequest,
  type CodingSessionDetail,
  type CodingSessionList,
  type CompanyDetail,
  type CompanyList,
  type CompanyMockStart,
  type CurrentUser,
  type Dashboard,
  type Health,
  type InterviewCatalog,
  type InterviewDetail,
  type InterviewEventCreate,
  type InterviewList,
  type InterviewStart,
  type LeaderboardQuery,
  type MentorCatalog,
  type MentorMessageRequest,
  type MentorSessionDetail,
  type MentorSessionList,
  type MentorStart,
  type Profile,
  type ProfileUpdate,
  type PublicLeaderboard,
  type Readiness,
  type ResumeDetail,
  type ResumeDownload,
  type ResumeList,
  type RoadmapDetail,
  type RoadmapGenerate,
  type RoadmapList,
  type RoadmapRegenerate,
  type StatsUpdate,
  type UserStats,
} from '@/lib/api/schemas';

/**
 * Every backend call the app can make, in one typed surface.
 *
 * Components never build URLs or pick schemas; they call these functions. That
 * keeps route strings in a single file and makes the full API surface reviewable
 * at a glance.
 */

const V1 = '/api/v1';

/** Empty body for 204 responses. */
const emptySchema = z.null();

/** Analysis can take a while on Groq; a short client timeout would abort a good run. */
const RESUME_UPLOAD_TIMEOUT_MS = 120_000;

export const api = {
  health: {
    /** Liveness. Public, so no token is attached. */
    live: (signal?: AbortSignal): Promise<Health> =>
      apiClient.get(`${V1}/health`, {
        schema: healthSchema,
        authenticated: false,
        ...(signal ? { signal } : {}),
      }),

    /**
     * Readiness, including per-dependency status.
     *
     * 503 is accepted rather than thrown: a degraded backend still returns a
     * complete report, and that report is precisely what the status page exists
     * to show.
     */
    ready: (signal?: AbortSignal): Promise<Readiness> =>
      apiClient.get(`${V1}/health/ready`, {
        schema: readinessSchema,
        authenticated: false,
        acceptStatuses: [503],
        ...(signal ? { signal } : {}),
      }),
  },

  me: {
    /** Profile and stats in one round-trip, for app boot. */
    get: (signal?: AbortSignal): Promise<CurrentUser> =>
      apiClient.get(`${V1}/me`, {
        schema: currentUserSchema,
        ...(signal ? { signal } : {}),
      }),

    stats: (signal?: AbortSignal): Promise<UserStats> =>
      apiClient.get(`${V1}/me/stats`, {
        schema: userStatsSchema,
        ...(signal ? { signal } : {}),
      }),

    dashboard: (signal?: AbortSignal): Promise<Dashboard> =>
      apiClient.get(`${V1}/me/dashboard`, {
        schema: dashboardSchema,
        ...(signal ? { signal } : {}),
      }),

    presence: (body: PresenceHeartbeatPayload): Promise<PresenceHeartbeat> =>
      apiClient.post(`${V1}/me/presence`, {
        schema: presenceHeartbeatSchema,
        body: {
          path: body.path,
          interview_id: body.interview_id ?? null,
        },
      }),

    trackAnalytics: (body: AnalyticsEventPayload): Promise<AnalyticsEventIngest> =>
      apiClient.post(`${V1}/me/analytics/events`, {
        schema: analyticsEventIngestSchema,
        body: {
          session_id: body.session_id,
          event_name: body.event_name,
          path: body.path,
          properties: body.properties ?? {},
        },
      }),

    /** Partial update; omitted keys are left untouched server-side. */
    update: (changes: ProfileUpdate): Promise<Profile> =>
      apiClient.patch(`${V1}/me`, { schema: profileSchema, body: changes }),

    /** Update client-owned gamification preferences. */
    updateStats: (changes: StatsUpdate): Promise<UserStats> =>
      apiClient.patch(`${V1}/me/stats`, { schema: userStatsSchema, body: changes }),

    /** Request that an admin delete this account. Does not erase data. */
    requestDeletion: (): Promise<Profile> =>
      apiClient.post(`${V1}/me/deletion-request`, { schema: profileSchema }),

    /** Cancel a pending deletion request. */
    cancelDeletionRequest: (): Promise<Profile> =>
      apiClient.delete(`${V1}/me/deletion-request`, { schema: profileSchema }),
  },

  admin: {
    /** Confirms the caller is an admin. Non-admins get 403. */
    me: (signal?: AbortSignal): Promise<AdminMe> =>
      apiClient.get(`${V1}/admin/me`, {
        schema: adminMeSchema,
        ...(signal ? { signal } : {}),
      }),

    presence: (signal?: AbortSignal): Promise<AdminPresence> =>
      apiClient.get(`${V1}/admin/presence`, {
        schema: adminPresenceSchema,
        ...(signal ? { signal } : {}),
      }),

    analytics: (days = 14, signal?: AbortSignal): Promise<AdminAnalytics> =>
      apiClient.get(`${V1}/admin/analytics`, {
        schema: adminAnalyticsSchema,
        query: { days },
        ...(signal ? { signal } : {}),
      }),

    system: (signal?: AbortSignal): Promise<AdminSystem> =>
      apiClient.get(`${V1}/admin/system`, {
        schema: adminSystemSchema,
        ...(signal ? { signal } : {}),
      }),

    auditLog: (params: AdminAuditLogQuery = {}, signal?: AbortSignal): Promise<AdminAuditLogList> =>
      apiClient.get(`${V1}/admin/audit-log`, {
        schema: adminAuditLogListSchema,
        query: {
          limit: params.limit,
          offset: params.offset,
        },
        ...(signal ? { signal } : {}),
      }),

    users: (params: AdminUsersQuery = {}, signal?: AbortSignal): Promise<AdminUserList> => {
      const search = new URLSearchParams();
      if (params.q) search.set('q', params.q);
      if (params.role) search.set('role', params.role);
      if (params.status) search.set('status', params.status);
      if (params.sort) search.set('sort', params.sort);
      if (params.direction) search.set('direction', params.direction);
      if (params.limit != null) search.set('limit', String(params.limit));
      if (params.offset != null) search.set('offset', String(params.offset));
      const qs = search.toString();
      return apiClient.get(`${V1}/admin/users${qs ? `?${qs}` : ''}`, {
        schema: adminUserListSchema,
        ...(signal ? { signal } : {}),
      });
    },

    user: (userId: string, signal?: AbortSignal): Promise<AdminUserDetail> =>
      apiClient.get(`${V1}/admin/users/${userId}`, {
        schema: adminUserDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    exportUsersCsvUrl: (params: AdminUsersQuery = {}): string => {
      const search = new URLSearchParams();
      if (params.q) search.set('q', params.q);
      if (params.role) search.set('role', params.role);
      if (params.status) search.set('status', params.status);
      if (params.sort) search.set('sort', params.sort);
      if (params.direction) search.set('direction', params.direction);
      const qs = search.toString();
      return `${V1}/admin/users/export.csv${qs ? `?${qs}` : ''}`;
    },

    updateStatus: (userId: string, account_status: AccountStatus): Promise<AdminUserListItem> =>
      apiClient.patch(`${V1}/admin/users/${userId}/status`, {
        schema: adminUserListItemSchema,
        body: { account_status },
      }),

    updateRole: (userId: string, role: AppRole): Promise<AdminUserListItem> =>
      apiClient.patch(`${V1}/admin/users/${userId}/role`, {
        schema: adminUserListItemSchema,
        body: { role },
      }),

    cancelDeletion: (userId: string): Promise<AdminUserListItem> =>
      apiClient.post(`${V1}/admin/users/${userId}/cancel-deletion`, {
        schema: adminUserListItemSchema,
        body: {},
      }),

    resetPassword: (userId: string): Promise<{ message: string }> =>
      apiClient.post(`${V1}/admin/users/${userId}/reset-password`, {
        schema: z.object({ message: z.string() }),
        body: {},
      }),

    deleteUser: (userId: string, confirmation: string): Promise<null> =>
      apiClient.delete(`${V1}/admin/users/${userId}`, {
        schema: emptySchema,
        body: { confirmation },
      }),
  },

  resumes: {
    list: (signal?: AbortSignal): Promise<ResumeList> =>
      apiClient.get(`${V1}/resumes`, {
        schema: resumeListSchema,
        ...(signal ? { signal } : {}),
      }),

    get: (id: string, signal?: AbortSignal): Promise<ResumeDetail> =>
      apiClient.get(`${V1}/resumes/${id}`, {
        schema: resumeDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    upload: (file: File, targetRole?: string, signal?: AbortSignal): Promise<ResumeDetail> => {
      const form = new FormData();
      form.append('file', file);
      if (targetRole?.trim()) form.append('target_role', targetRole.trim());
      return apiClient.post(`${V1}/resumes`, {
        schema: resumeDetailSchema,
        formData: form,
        timeoutMs: RESUME_UPLOAD_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      });
    },

    download: (id: string, signal?: AbortSignal): Promise<ResumeDownload> =>
      apiClient.get(`${V1}/resumes/${id}/download`, {
        schema: resumeDownloadSchema,
        ...(signal ? { signal } : {}),
      }),

    delete: (id: string): Promise<null> =>
      apiClient.delete(`${V1}/resumes/${id}`, { schema: emptySchema }),
  },

  interviews: {
    catalog: (signal?: AbortSignal): Promise<InterviewCatalog> =>
      apiClient.get(`${V1}/interviews/catalog`, {
        schema: interviewCatalogSchema,
        authenticated: false,
        ...(signal ? { signal } : {}),
      }),

    list: (signal?: AbortSignal): Promise<InterviewList> =>
      apiClient.get(`${V1}/interviews`, {
        schema: interviewListSchema,
        ...(signal ? { signal } : {}),
      }),

    start: (body: InterviewStart): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/interviews`, {
        schema: interviewDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    get: (id: string, signal?: AbortSignal): Promise<InterviewDetail> =>
      apiClient.get(`${V1}/interviews/${id}`, {
        schema: interviewDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    answer: (id: string, body: AnswerSubmit): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/interviews/${id}/answers`, {
        schema: interviewDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    recordEvents: (id: string, events: InterviewEventCreate[]): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/interviews/${id}/events`, {
        schema: interviewDetailSchema,
        body: { events },
      }),

    complete: (id: string): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/interviews/${id}/complete`, {
        schema: interviewDetailSchema,
        timeoutMs: 120_000,
      }),

    abandon: (id: string): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/interviews/${id}/abandon`, {
        schema: interviewDetailSchema,
      }),
  },

  coding: {
    listProblems: (signal?: AbortSignal): Promise<CodingProblemList> =>
      apiClient.get(`${V1}/coding/problems`, {
        schema: codingProblemListSchema,
        ...(signal ? { signal } : {}),
      }),

    getProblem: (slug: string, signal?: AbortSignal): Promise<CodingProblemDetail> =>
      apiClient.get(`${V1}/coding/problems/${slug}`, {
        schema: codingProblemDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    listSessions: (signal?: AbortSignal): Promise<CodingSessionList> =>
      apiClient.get(`${V1}/coding/sessions`, {
        schema: codingSessionListSchema,
        ...(signal ? { signal } : {}),
      }),

    startSession: (slug: string): Promise<CodingSessionDetail> =>
      apiClient.post(`${V1}/coding/problems/${slug}/sessions`, {
        schema: codingSessionDetailSchema,
      }),

    getSession: (id: string, signal?: AbortSignal): Promise<CodingSessionDetail> =>
      apiClient.get(`${V1}/coding/sessions/${id}`, {
        schema: codingSessionDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    revealHint: (id: string): Promise<CodingSessionDetail> =>
      apiClient.post(`${V1}/coding/sessions/${id}/hint`, {
        schema: codingSessionDetailSchema,
      }),

    run: (id: string, body: CodingRunRequest): Promise<CodingSessionDetail> =>
      apiClient.post(`${V1}/coding/sessions/${id}/run`, {
        schema: codingSessionDetailSchema,
        body,
        timeoutMs: 60_000,
      }),

    submit: (id: string, body: CodingRunRequest): Promise<CodingSessionDetail> =>
      apiClient.post(`${V1}/coding/sessions/${id}/submit`, {
        schema: codingSessionDetailSchema,
        body,
        timeoutMs: 60_000,
      }),

    abandon: (id: string): Promise<CodingSessionDetail> =>
      apiClient.post(`${V1}/coding/sessions/${id}/abandon`, {
        schema: codingSessionDetailSchema,
      }),
  },

  mentor: {
    catalog: (signal?: AbortSignal): Promise<MentorCatalog> =>
      apiClient.get(`${V1}/mentor/catalog`, {
        schema: mentorCatalogSchema,
        authenticated: false,
        ...(signal ? { signal } : {}),
      }),

    listSessions: (signal?: AbortSignal): Promise<MentorSessionList> =>
      apiClient.get(`${V1}/mentor/sessions`, {
        schema: mentorSessionListSchema,
        ...(signal ? { signal } : {}),
      }),

    start: (body: MentorStart): Promise<MentorSessionDetail> =>
      apiClient.post(`${V1}/mentor/sessions`, {
        schema: mentorSessionDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    getSession: (id: string, signal?: AbortSignal): Promise<MentorSessionDetail> =>
      apiClient.get(`${V1}/mentor/sessions/${id}`, {
        schema: mentorSessionDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    sendMessage: (id: string, body: MentorMessageRequest): Promise<MentorSessionDetail> =>
      apiClient.post(`${V1}/mentor/sessions/${id}/messages`, {
        schema: mentorSessionDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    close: (id: string): Promise<MentorSessionDetail> =>
      apiClient.post(`${V1}/mentor/sessions/${id}/close`, {
        schema: mentorSessionDetailSchema,
      }),
  },

  roadmap: {
    list: (signal?: AbortSignal): Promise<RoadmapList> =>
      apiClient.get(`${V1}/roadmap`, {
        schema: roadmapListSchema,
        ...(signal ? { signal } : {}),
      }),

    generate: (body: RoadmapGenerate): Promise<RoadmapDetail> =>
      apiClient.post(`${V1}/roadmap`, {
        schema: roadmapDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    get: (id: string, signal?: AbortSignal): Promise<RoadmapDetail> =>
      apiClient.get(`${V1}/roadmap/${id}`, {
        schema: roadmapDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    regenerate: (id: string, body: RoadmapRegenerate = {}): Promise<RoadmapDetail> =>
      apiClient.post(`${V1}/roadmap/${id}/regenerate`, {
        schema: roadmapDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    archive: (id: string): Promise<RoadmapDetail> =>
      apiClient.post(`${V1}/roadmap/${id}/archive`, {
        schema: roadmapDetailSchema,
      }),

    completeMilestone: (roadmapId: string, milestoneId: string): Promise<RoadmapDetail> =>
      apiClient.post(`${V1}/roadmap/${roadmapId}/milestones/${milestoneId}/complete`, {
        schema: roadmapDetailSchema,
      }),

    skipMilestone: (roadmapId: string, milestoneId: string): Promise<RoadmapDetail> =>
      apiClient.post(`${V1}/roadmap/${roadmapId}/milestones/${milestoneId}/skip`, {
        schema: roadmapDetailSchema,
      }),
  },

  companies: {
    list: (signal?: AbortSignal): Promise<CompanyList> =>
      apiClient.get(`${V1}/companies`, {
        schema: companyListSchema,
        ...(signal ? { signal } : {}),
      }),

    get: (slug: string, signal?: AbortSignal): Promise<CompanyDetail> =>
      apiClient.get(`${V1}/companies/${slug}`, {
        schema: companyDetailSchema,
        ...(signal ? { signal } : {}),
      }),

    startMock: (slug: string, body: CompanyMockStart = {}): Promise<InterviewDetail> =>
      apiClient.post(`${V1}/companies/${slug}/mock`, {
        schema: interviewDetailSchema,
        body,
        timeoutMs: 120_000,
      }),

    completeChecklist: (slug: string, itemCode: string): Promise<CompanyDetail> =>
      apiClient.post(`${V1}/companies/${slug}/checklist/${itemCode}/complete`, {
        schema: companyDetailSchema,
      }),

    uncompleteChecklist: (slug: string, itemCode: string): Promise<CompanyDetail> =>
      apiClient.post(`${V1}/companies/${slug}/checklist/${itemCode}/uncomplete`, {
        schema: companyDetailSchema,
      }),
  },

  leaderboard: {
    list: (query: LeaderboardQuery = {}, signal?: AbortSignal): Promise<PublicLeaderboard> =>
      apiClient.get(`${V1}/leaderboard`, {
        schema: publicLeaderboardSchema,
        query: {
          limit: query.limit,
          offset: query.offset,
        },
        ...(signal ? { signal } : {}),
      }),
  },
} as const;
